import {
  SurveyAnswerDto,
  SurveyDto,
  SurveyOptionDto,
  SurveyQuestionDto,
  SurveyQuestionType,
  SurveyResponseDto,
  SurveyResponseStatus,
  SurveyStatus,
} from '../../api/models/fraud.models';

export function getSurveyQuestions(survey: SurveyDto | null | undefined): SurveyQuestionDto[] {
  if (!survey || !Array.isArray(survey.questions)) {
    return [];
  }

  return survey.questions;
}

function normalizeQuestionType(type: unknown): SurveyQuestionType {
  const normalized = String(type ?? 'FREE_TEXT').toUpperCase().replace(/-/g, '_');

  if (
    normalized === 'SINGLE_CHOICE'
    || normalized === 'MULTI_CHOICE'
    || normalized === 'DROP_DOWN'
    || normalized === 'FREE_TEXT'
  ) {
    return normalized;
  }

  return 'FREE_TEXT';
}

/** Single-select option questions (radio cards or dropdown). */
export function isSingleSelectQuestionType(type: SurveyQuestionType): boolean {
  return type === 'SINGLE_CHOICE' || type === 'DROP_DOWN';
}

/** Questions answered via optionIds (optionally plus custom textValue). */
export function isOptionBasedQuestionType(type: SurveyQuestionType): boolean {
  return type === 'SINGLE_CHOICE' || type === 'MULTI_CHOICE' || type === 'DROP_DOWN';
}

const MAX_ICON_SVG_LENGTH = 20_000;

/**
 * Coarse SVG safety check mirroring the backend (`validateIconSvg`).
 * Returns trimmed markup or null when missing/invalid.
 */
export function sanitizeSurveyIconSvg(raw: string | null | undefined): string | null {
  if (raw == null) {
    return null;
  }

  const trimmed = String(raw).trim();
  if (!trimmed) {
    return null;
  }

  const lower = trimmed.toLowerCase();
  if (!lower.startsWith('<svg') || !lower.endsWith('</svg>')) {
    return null;
  }

  if (trimmed.length > MAX_ICON_SVG_LENGTH) {
    return null;
  }

  if (/<script|javascript:|\son\w+\s*=/i.test(trimmed)) {
    return null;
  }

  return trimmed;
}

function normalizeSurveyOption(raw: unknown): SurveyOptionDto {
  const source = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const iconSvgRaw = source['iconSvg'];
  const iconSvg = typeof iconSvgRaw === 'string' ? sanitizeSurveyIconSvg(iconSvgRaw) : null;

  return {
    id: Number(source['id']),
    position: Number(source['position'] ?? 0),
    text: String(source['text'] ?? ''),
    nextQuestionId: source['nextQuestionId'] != null ? Number(source['nextQuestionId']) : null,
    allowsCustomText: source['allowsCustomText'] === true,
    iconSvg,
  };
}

export function getCustomTextOption(
  question: SurveyQuestionDto | null | undefined,
): SurveyOptionDto | undefined {
  return question?.options?.find((option) => option.allowsCustomText);
}

export function isCustomTextOptionSelected(
  question: SurveyQuestionDto | null | undefined,
  value: string | number | number[] | null | undefined,
): boolean {
  const customOption = getCustomTextOption(question);
  if (!customOption) {
    return false;
  }

  if (Array.isArray(value)) {
    return value.includes(customOption.id);
  }

  return value !== '' && value != null && Number(value) === customOption.id;
}

export function questionHasIconOptions(question: SurveyQuestionDto | null | undefined): boolean {
  return question?.options?.some((option) => !!option.iconSvg) ?? false;
}

function normalizeSurveyQuestion(raw: unknown): SurveyQuestionDto {
  const source = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const optionsSource = source['options'];
  const options = Array.isArray(optionsSource)
    ? optionsSource.map(normalizeSurveyOption)
    : undefined;

  return {
    id: Number(source['id']),
    position: Number(source['position'] ?? 0),
    type: normalizeQuestionType(source['type']),
    question: String(source['text'] ?? source['question'] ?? ''),
    required: source['required'] !== false,
    nextQuestionId: source['nextQuestionId'] != null ? Number(source['nextQuestionId']) : null,
    options,
  };
}

export function isMeaningfulRewardAmount(amount: string | number | undefined | null): boolean {
  if (amount === undefined || amount === null || amount === '') {
    return false;
  }

  const num = typeof amount === 'string' ? Number.parseFloat(amount) : amount;
  return Number.isFinite(num) && num > 0;
}

function normalizeSurveyResponseStatus(status: unknown): SurveyResponseStatus {
  const normalized = String(status ?? '').toUpperCase();
  return normalized === 'COMPLETED' ? 'COMPLETED' : 'IN_PROGRESS';
}

function normalizeSurveyStatus(status: unknown): SurveyStatus {
  const normalized = String(status ?? '').toUpperCase();
  if (normalized === 'DRAFT' || normalized === 'PUBLISHED' || normalized === 'ARCHIVED') {
    return normalized;
  }

  return 'PUBLISHED';
}

function normalizeSurveyResponse(raw: unknown): SurveyResponseDto | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const source = raw as Record<string, unknown>;
  const answers = Array.isArray(source['answers']) ? source['answers'] as SurveyAnswerDto[] : [];

  return {
    id: Number(source['id']),
    status: normalizeSurveyResponseStatus(source['status']),
    rewardAmount: source['rewardAmount'] != null && source['rewardAmount'] !== ''
      ? Number(source['rewardAmount'])
      : null,
    completedAt: source['completedAt'] != null ? String(source['completedAt']) : null,
    answers,
  };
}

export function normalizeSurveyDto(raw: unknown): SurveyDto {
  const root = raw && typeof raw === 'object' ? raw as Record<string, unknown> : {};
  const payload = root['data'] && typeof root['data'] === 'object'
    ? root['data'] as Record<string, unknown>
    : root;

  const surveyRaw = payload['survey'];
  const surveySource = surveyRaw && typeof surveyRaw === 'object'
    ? surveyRaw as Record<string, unknown>
    : payload;

  const questionsSource = surveySource['questions'];
  const questions = Array.isArray(questionsSource)
    ? questionsSource.map(normalizeSurveyQuestion)
    : [];

  const response = normalizeSurveyResponse(payload['response'] ?? surveySource['response']);

  return {
    id: Number(surveySource['id']),
    title: String(surveySource['title'] ?? ''),
    description: surveySource['description'] ? String(surveySource['description']) : undefined,
    status: normalizeSurveyStatus(surveySource['status']),
    rewardAmount: Number(surveySource['rewardAmount'] ?? 0),
    priority: Number(surveySource['priority'] ?? 0),
    createdAt: String(surveySource['createdAt'] ?? ''),
    publishedAt: surveySource['publishedAt'] != null ? String(surveySource['publishedAt']) : null,
    questions,
    targeting: surveySource['targeting'] ?? null,
    response,
  };
}

export function getSurveyQuestionById(survey: SurveyDto, questionId: number): SurveyQuestionDto | undefined {
  return getSurveyQuestions(survey).find((question) => question.id === questionId);
}

export function getSortedSurveyQuestions(survey: SurveyDto): SurveyQuestionDto[] {
  return [...getSurveyQuestions(survey)].sort((a, b) => a.position - b.position);
}

export function isSurveyAnswerProvided(
  answer: SurveyAnswerDto | undefined,
  question?: SurveyQuestionDto,
): boolean {
  if (!answer) {
    return false;
  }

  if (question && isOptionBasedQuestionType(question.type)) {
    const optionIds = answer.optionIds ?? [];
    if (!optionIds.length) {
      return false;
    }

    const customOption = getCustomTextOption(question);
    if (customOption && optionIds.includes(customOption.id)) {
      return answer.textValue != null && String(answer.textValue).trim().length > 0;
    }

    return true;
  }

  if (answer.textValue != null && String(answer.textValue).trim().length > 0) {
    return true;
  }

  return (answer.optionIds?.length ?? 0) > 0;
}

function getNextQuestionIdByPosition(survey: SurveyDto, questionId: number): number | null {
  const questions = getSortedSurveyQuestions(survey);
  const index = questions.findIndex((question) => question.id === questionId);

  if (index < 0 || index >= questions.length - 1) {
    return null;
  }

  return questions[index + 1].id;
}

/** Every question some other question or option branches into. */
function getBranchTargetQuestionIds(survey: SurveyDto): Set<number> {
  const targets = new Set<number>();

  for (const question of getSurveyQuestions(survey)) {
    if (question.nextQuestionId != null) {
      targets.add(question.nextQuestionId);
    }

    for (const option of question.options ?? []) {
      if (option.nextQuestionId != null) {
        targets.add(option.nextQuestionId);
      }
    }
  }

  return targets;
}

/** Entry point of the survey: the first question nothing branches into. */
export function getSurveyRootQuestionId(survey: SurveyDto): number | null {
  const questions = getSortedSurveyQuestions(survey);
  if (!questions.length) {
    return null;
  }

  const targets = getBranchTargetQuestionIds(survey);
  const root = questions.find((question) => !targets.has(question.id));
  return (root ?? questions[0]).id;
}

/** True when the survey declares any `nextQuestionId`, so position order is not the flow. */
export function surveyHasBranching(survey: SurveyDto): boolean {
  return getSurveyQuestions(survey).some(
    (question) => question.nextQuestionId != null
      || (question.options ?? []).some((option) => option.nextQuestionId != null),
  );
}

/** Drops branch targets that no longer exist in the payload. */
function resolveExistingQuestionId(survey: SurveyDto, questionId: number | null): number | null {
  if (questionId == null) {
    return null;
  }

  return getSurveyQuestionById(survey, questionId) ? questionId : null;
}

function getSelectedOptionNextQuestionId(
  survey: SurveyDto,
  question: SurveyQuestionDto,
  answer: SurveyAnswerDto,
): number | null {
  if (!question.options?.length) {
    return null;
  }

  const selectedIds = answer.optionIds ?? [];
  // Option order decides the branch so multi-choice answers stay deterministic.
  const orderedOptions = [...question.options].sort((a, b) => a.position - b.position);

  for (const option of orderedOptions) {
    if (!selectedIds.includes(option.id)) {
      continue;
    }

    const target = resolveExistingQuestionId(survey, option.nextQuestionId);
    if (target != null) {
      return target;
    }
  }

  return null;
}

/**
 * Immediate next question for a given answer: the question level `nextQuestionId` wins,
 * then the `nextQuestionId` of the selected option(s).
 * When neither is set the chain ends, unless the survey has no branching at all —
 * those legacy surveys keep walking by position.
 */
export function resolveNextQuestionId(
  survey: SurveyDto,
  question: SurveyQuestionDto,
  answer: SurveyAnswerDto,
): number | null {
  const direct = resolveExistingQuestionId(survey, question.nextQuestionId);
  if (direct != null) {
    return direct;
  }

  const optionTarget = getSelectedOptionNextQuestionId(survey, question, answer);
  if (optionTarget != null) {
    return optionTarget;
  }

  return surveyHasBranching(survey) ? null : getNextQuestionIdByPosition(survey, question.id);
}

/** Next questions reachable from a question while its answer is still unknown. */
function getPotentialNextQuestionIds(survey: SurveyDto, question: SurveyQuestionDto): number[] {
  const direct = resolveExistingQuestionId(survey, question.nextQuestionId);
  if (direct != null) {
    return [direct];
  }

  const branches: number[] = [];
  for (const option of question.options ?? []) {
    const target = resolveExistingQuestionId(survey, option.nextQuestionId);
    if (target != null && !branches.includes(target)) {
      branches.push(target);
    }
  }

  if (branches.length) {
    return branches;
  }

  const positionalId = surveyHasBranching(survey)
    ? null
    : getNextQuestionIdByPosition(survey, question.id);
  return positionalId == null ? [] : [positionalId];
}

/**
 * Counts the questions from `questionId` to the end of the chain, that question included.
 * An answered question follows its real branch; an unanswered one assumes the longest
 * branch it could still take, so the number only shrinks as the user picks answers.
 */
function countChainFrom(
  survey: SurveyDto,
  questionId: number,
  answers: ReadonlyMap<number, SurveyAnswerDto>,
  visitedIds: Set<number>,
): number {
  const question = visitedIds.has(questionId) ? undefined : getSurveyQuestionById(survey, questionId);
  if (!question) {
    return 0;
  }

  const nextVisitedIds = new Set(visitedIds).add(questionId);
  const answer = answers.get(questionId);

  if (answer && isSurveyAnswerProvided(answer, question)) {
    const nextId = resolveNextQuestionId(survey, question, answer);
    return nextId == null ? 1 : countChainFrom(survey, nextId, answers, nextVisitedIds) + 1;
  }

  let longestTail = 0;
  for (const nextId of getPotentialNextQuestionIds(survey, question)) {
    longestTail = Math.max(longestTail, countChainFrom(survey, nextId, answers, nextVisitedIds));
  }

  return longestTail + 1;
}

/**
 * How many questions are left in the chain, the current one included.
 * `pendingAnswer` is the not yet saved answer of the current step, so the count reacts
 * to a selection before it is submitted.
 */
export function getRemainingChainLength(
  survey: SurveyDto,
  question: SurveyQuestionDto,
  pendingAnswer: SurveyAnswerDto | null,
  answers?: ReadonlyMap<number, SurveyAnswerDto>,
): number {
  const chainAnswers = new Map(answers ?? []);

  if (pendingAnswer) {
    chainAnswers.set(question.id, pendingAnswer);
  } else {
    chainAnswers.delete(question.id);
  }

  return countChainFrom(survey, question.id, chainAnswers, new Set());
}

export function buildSurveyAnswer(
  question: SurveyQuestionDto,
  value: string | number | number[],
  customText?: string,
): SurveyAnswerDto {
  if (question.type === 'FREE_TEXT') {
    return {
      questionId: question.id,
      textValue: String(value),
    };
  }

  const optionIds = Array.isArray(value)
    ? value.map(Number)
    : value === '' || value == null
      ? []
      : [Number(value)];

  const answer: SurveyAnswerDto = {
    questionId: question.id,
    optionIds,
  };

  if (isCustomTextOptionSelected(question, optionIds) && customText != null) {
    answer.textValue = String(customText);
  }

  return answer;
}

/** Empty answer payload so the backend treats the question as unanswered. */
export function buildClearedSurveyAnswer(question: SurveyQuestionDto): SurveyAnswerDto {
  if (question.type === 'FREE_TEXT') {
    return {
      questionId: question.id,
      textValue: '',
    };
  }

  return {
    questionId: question.id,
    optionIds: [],
  };
}

/**
 * Walks the chain from the root while stored answers keep resolving a next question.
 * The returned path ends on the first unanswered question, or on the last answered one
 * when the chain is complete.
 */
function walkAnsweredChain(
  survey: SurveyDto,
  answers: ReadonlyMap<number, SurveyAnswerDto>,
): number[] {
  const path: number[] = [];
  const visitedIds = new Set<number>();
  let currentId = getSurveyRootQuestionId(survey);

  while (currentId != null && !visitedIds.has(currentId)) {
    const question = getSurveyQuestionById(survey, currentId);
    if (!question) {
      break;
    }

    visitedIds.add(currentId);
    path.push(currentId);

    const answer = answers.get(currentId);
    if (!isSurveyAnswerProvided(answer, question)) {
      break;
    }

    currentId = resolveNextQuestionId(survey, question, answer!);
  }

  return path;
}

export function findResumeQuestionId(
  survey: SurveyDto,
  answers: ReadonlyMap<number, SurveyAnswerDto>,
): number | null {
  const path = walkAnsweredChain(survey, answers);
  return path.length ? path[path.length - 1] : getSurveyRootQuestionId(survey);
}

/** Steps leading to `targetQuestionId`, so Back can retrace the branch the user took. */
export function buildAnswerPath(
  survey: SurveyDto,
  answers: ReadonlyMap<number, SurveyAnswerDto>,
  targetQuestionId: number,
): number[] {
  if (!getSurveyQuestionById(survey, targetQuestionId)) {
    return [];
  }

  const path = walkAnsweredChain(survey, answers);
  const targetIndex = path.indexOf(targetQuestionId);

  return targetIndex >= 0 ? path.slice(0, targetIndex + 1) : [...path, targetQuestionId];
}
